/**
 * FigmaColorField — 관리자 색상 텍스트 입력을 Figma 스타일 컬러 피커로 in-place 강화한다.
 *
 * 서드파티(Pickr)는 rule-02(third-party wrapper)에 따라 PickrWrapper 로만 접근한다.
 * 기존 colorPickerField(.admin-cpf, div+hidden-input, solid/gradient 탭)와는 별개의
 * 가볍고 단순한 래퍼로, 인스펙터의 단일 hex 필드를 그대로 강화한다.
 *
 * 마운트 규약(마크업):
 *   <input class="admin-form__input" data-figma-color value="#111111">
 *   alpha 를 별도 필드에 동기화하려면(예: 오버레이 색상 ↔ 오버레이 투명도):
 *   <input ... data-figma-color
 *          data-figma-color-opacity="section_style[background][overlay][opacity]">
 *
 * 값 계약: 원래 input 에는 항상 `#RRGGBB`(6자리 hex)만 기록한다(서버 settings_json 계약 유지).
 *          alpha 는 data-figma-color-opacity 대상 필드(0~1)에만 동기화한다.
 */
import { PickrWrapper, type PickrInstance, type PickrHSVaColor } from "../../components/PickrWrapper";

const HEX_ANY = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// 자주 쓰는 견본(중립 + 브랜드 계열). Figma "On this page" 팔레트 대체.
const SWATCHES = [
    "#000000", "#1a1a1a", "#374151", "#6b7280", "#9ca3af", "#e5e7eb", "#ffffff",
    "#1d4ed8", "#2563eb", "#0ea5e9", "#059669", "#16a34a",
    "#d97706", "#dc2626", "#db2777", "#7c3aed",
];

const I18N = {
    "ui:dialog": "색상 선택",
    "btn:toggle": "색상 선택 열기",
    "btn:swatch": "색상 견본",
    "btn:last-color": "이전 색상",
    "aria:input": "색상 코드 입력",
    "aria:palette": "명도·채도",
    "aria:hue": "색상(hue)",
    "aria:opacity": "투명도",
};

interface EyeDropperCtor {
    new (): { open: () => Promise<{ sRGBHex: string }> };
}

function normalizeHex(value: string): string | null {
    const s = value.trim();
    if (!HEX_ANY.test(s)) return null;
    if (s.length === 4) {
        return ("#" + s.slice(1).split("").map((c) => c + c).join("")).toLowerCase();
    }
    return s.toLowerCase();
}

function clampAlpha(value: string | null | undefined): number {
    const n = Number.parseFloat(String(value ?? ""));
    if (!Number.isFinite(n)) return 1;
    return Math.min(1, Math.max(0, n));
}

function buildField(input: HTMLInputElement): void {
    if (input.dataset.figmaColorReady === "1") return;
    input.dataset.figmaColorReady = "1";

    const opacityName = input.getAttribute("data-figma-color-opacity") ?? "";
    const withOpacity = opacityName !== "";
    const opacityInput = withOpacity
        ? (input.form?.querySelector<HTMLInputElement>(`[name="${opacityName}"]`) ?? null)
        : null;

    const initialHex = normalizeHex(input.value) ?? "";
    const initialAlpha = withOpacity ? clampAlpha(opacityInput?.value) : 1;

    // 래퍼: [스와치 트리거] [hex input] [스포이드]
    const wrap = document.createElement("div");
    wrap.className = "fcf";
    input.parentNode?.insertBefore(wrap, input);

    // Pickr 는 넘긴 el 을 자신의 .pickr 루트로 교체하므로 내부 div 를 넘겨 .fcf__swatch 를 보존한다.
    const swatch = document.createElement("div");
    swatch.className = "fcf__swatch";
    const swatchInner = document.createElement("div");
    swatch.appendChild(swatchInner);
    wrap.appendChild(swatch);

    input.classList.add("fcf__hex");
    input.autocomplete = "off";
    input.spellcheck = false;
    wrap.appendChild(input);

    const EyeDropper = (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper;
    let eyeBtn: HTMLButtonElement | null = null;
    if (EyeDropper) {
        eyeBtn = document.createElement("button");
        eyeBtn.type = "button";
        eyeBtn.className = "fcf__eyedropper";
        eyeBtn.title = "화면에서 색상 추출";
        eyeBtn.setAttribute("aria-label", "화면에서 색상 추출");
        eyeBtn.innerHTML = '<i class="fa-solid fa-eye-dropper" aria-hidden="true"></i>';
        wrap.appendChild(eyeBtn);
    }

    const pickr: PickrInstance = PickrWrapper.create({
        el: swatchInner,
        theme: "nano",
        appClass: "fcf-app",
        default: initialHex || "#000000",
        defaultRepresentation: "HEX",
        lockOpacity: !withOpacity,
        position: "bottom-start",
        i18n: I18N,
        swatches: SWATCHES,
        components: {
            preview: true,
            opacity: withOpacity,
            hue: true,
            interaction: {
                hex: true,
                rgba: withOpacity,
                hsla: false,
                hsva: false,
                input: true,
                cancel: false,
                clear: false,
                save: false,
            },
        },
    });

    // 오버레이 초기 alpha 반영(색상 필드는 6자리 hex 유지).
    if (withOpacity && initialHex && initialAlpha < 1) {
        const aa = Math.round(initialAlpha * 255).toString(16).padStart(2, "0");
        pickr.setColor(initialHex + aa, true);
    }

    let syncing = false;

    const writeBack = (color: PickrHSVaColor | null, commit: boolean): void => {
        if (!color) return;
        syncing = true;
        input.value = color.toHEXA().toString().slice(0, 7).toLowerCase();
        input.dispatchEvent(new Event("input", { bubbles: true }));
        if (commit) input.dispatchEvent(new Event("change", { bubbles: true }));
        if (withOpacity && opacityInput) {
            opacityInput.value = String(Math.round(color.toRGBA()[3] * 100) / 100);
            opacityInput.dispatchEvent(new Event("input", { bubbles: true }));
            if (commit) opacityInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
        syncing = false;
    };

    pickr.on("change", (color: PickrHSVaColor | null) => writeBack(color, false));
    pickr.on("changestop", () => writeBack(pickr.getColor(), true));

    // hex 직접 타이핑 → 피커 동기(무한 루프 방지 위해 silent + syncing 가드).
    input.addEventListener("input", () => {
        if (syncing) return;
        const hex = normalizeHex(input.value);
        if (hex) pickr.setColor(hex, true);
    });

    if (eyeBtn && EyeDropper) {
        eyeBtn.addEventListener("click", () => {
            void (async () => {
                try {
                    const res = await new EyeDropper().open();
                    const hex = normalizeHex(res.sRGBHex);
                    if (!hex) return;
                    pickr.setColor(hex, true);
                    writeBack(pickr.getColor(), true);
                } catch {
                    /* 사용자 취소 — 무시 */
                }
            })();
        });
    }
}

export function initFigmaColorFields(scope: Document | HTMLElement = document): void {
    scope.querySelectorAll<HTMLInputElement>("input[data-figma-color]").forEach((input) => {
        buildField(input);
    });
}
